import React, { ReactElement, useEffect, useState, FC } from 'react';
import * as yup from 'yup';

import BaseModal from '../../../components/BaseModal/BaseModal';
import Forms from '../../../components/Forms/Forms';
import PropertyDisplay from '../../../components/PropertyDisplay/PropertyDisplay';
import { digitalCodesConstants } from '../../../constants/digitalCodesConstants';
import { postWSPRequestAction, postROMSizeRequestAction, postDPRequestAction } from '../../../services/digitalCodesService';
import { isEmptyObject } from '../../../utils/dataUtils';
import { getCurrentRequest } from '../../../utils/digitalCodesUtils';
import { createMessageForError, toasterNotify } from '../../../utils/toaster';
import { validateToSchema } from '../../../utils/validationUtils';

const { APPROVED } = digitalCodesConstants.priceRequestStatuses;

const schema = yup.object().shape({
	comment: yup.string().trim().required('A reason must be supplied to continue')
});

interface ExtendWSPLimitModalProps {
	show?: boolean;
	onClose?: () => void;
	onChange?: () => void;
	component?: ProductComponentData;
	priceRequests?: (CIBPriceRequestData | DeclaredPriceRequestData)[];
	rightsHolderId?: string;
}

const getRequestTypeAndApi = (priceRequests: any, rightsHolderId: any) => {
	const extendedRequest = getCurrentRequest(priceRequests, rightsHolderId);

	let extendedRequestType = 'Declared Price';
	let extendedAPI = 'postDPRequestAction';
	let extendedPriceRequestId = extendedRequest && 'declared_price_request_id' in extendedRequest ? extendedRequest.declared_price_request_id : null;
	if (extendedRequest && 'price_type_code' in extendedRequest) {
		extendedRequestType = 'Code-in-Box Price';
		extendedPriceRequestId = extendedRequest.cib_price_request_id;
		if (extendedRequest.price_type_code === digitalCodesConstants.priceTypeCodes.WHOLESALE) {
			extendedAPI = 'postWSPRequestAction';
		} else {
			extendedAPI = 'postROMSizeRequestAction';
		}
	}
	return { extendedRequestType, extendedAPI, extendedPriceRequestId };
};

const ExtendLimitModal: FC<ExtendWSPLimitModalProps> = ({
	show,
	onClose,
	onChange,
	component,
	priceRequests,
	rightsHolderId,
}: ExtendWSPLimitModalProps): ReactElement => {
	const [formValues, setFormValues] = useState<Record<string, any>>({});
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [showAllErrors, setShowAllErrors] = useState<boolean>(false);

	const { extendedRequestType, extendedAPI, extendedPriceRequestId } = getRequestTypeAndApi(priceRequests, rightsHolderId);

	useEffect(() => {
		if (show) {
			setShowAllErrors(false);
			setFormValues({});
		}
	}, [show]);

	const validationErrors = validateToSchema(schema, formValues);

	return (
		<BaseModal
			show={show || false}
			onCancel={onClose && (() => onClose())}
			isSubmitting={isSubmitting}
		>
			<BaseModal.Title>Allow For New {extendedRequestType} Agreement</BaseModal.Title>
			<PropertyDisplay noStripes label="NSUID">
				{component?.ns_uid}
			</PropertyDisplay>
			<PropertyDisplay noStripes label="Digital Item">
				{component?.game_name}
			</PropertyDisplay>
			<PropertyDisplay noStripes label="Number of Approved Requests">
				{priceRequests &&
					(priceRequests.filter(
						(request) =>
							request.ndid_company_id === rightsHolderId &&
							request.request_status === APPROVED,
					)?.length ||
						'0')}
			</PropertyDisplay>

			<Forms
				values={formValues || {}}
				onChange={(newFormValues) => setFormValues(newFormValues)}
				validationErrors={validationErrors}
				showAllErrors={showAllErrors}
				className="Forms__label-left"
			>
				<Forms.TextArea id="comment" rows={4}>
					<Forms.Heading>
						<span style={{ paddingLeft: '15px', fontWeight: 'bold' }}>Reason</span>
					</Forms.Heading>
				</Forms.TextArea>
			</Forms>

			<BaseModal.Submit
				disabled={!extendedPriceRequestId}
				onClick={async () => {
					if (isSubmitting) {
						return;
					}
					if (!isEmptyObject(validationErrors)) {
						setShowAllErrors(true);
						return;
					}

					setIsSubmitting(true);
					try {
						const apiMap: Record<string, (componentNsUid: string | number, payload: Record<string, any>) => Promise<any>> = { postWSPRequestAction, postROMSizeRequestAction, postDPRequestAction };
						await apiMap[extendedAPI](
							extendedPriceRequestId!,
							{
								action: digitalCodesConstants.priceRequestActions.EXTENDED_LIMIT,
								comment: formValues.comment,
							}
						);
						toasterNotify(
							`The limit on new ${extendedRequestType} requests was extended for "${component?.game_name}"`,
							'success',
						);
						onChange && onChange();
					} catch (error) {
						toasterNotify(
							createMessageForError(
								error as Error,
								`extending limits on ${extendedRequestType} requests`,
							),
							'error',
							error as Error,
						);
						setIsSubmitting(false);
					} finally {
						onClose && onClose();
					}
				}}
			>
				Confirm
			</BaseModal.Submit>
		</BaseModal>
	);
};

export default ExtendLimitModal;
